'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useDatadogTracker } from '@/hooks/publicSearch/datadog/useDatadogTracker';
import FilterGroupContainer from './FilterGroupContainer';
import { EventType } from '@/lib/types';
import { atom, useAtom } from 'jotai';
import { CircularProgress } from '@/primitives';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useSearchPrecheck } from '@/hooks/publicSearch/useSearchPrecheck';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { armSearchHistoryPush } from '@/hooks/publicSearch/usePublicSearchUrlSync';
import { abortAllPendingGraphQL } from '@/publicSearch/apolloClient';
import { FilterInput, LogicalOperator } from '../../constants';
import { SEARCH_TYPE } from '@/constants/searchType';
import { cancelPendingAiSearch } from '@/hooks/publicSearch/aiSearchRequest';

export const openAdvanceFilterDialogAtom = atom(false);
export const aiSearchLoadingAtom = atom(false);

export default function AdvancedFilterDialog() {
  const [open, setOpen] = useAtom(openAdvanceFilterDialogAtom);
  const [aiSearchLoading, setAiSearchLoading] = useAtom(aiSearchLoadingAtom);
  const searchType = useSearchType();
  const {
    filters,
    filterGroups,
    commonFilterOperator,
    groupsOperator,
    clearFilter,
    clearFilterGroups,
    addFilter,
    updateFilter,
    removeFilter,
    updateCommonFilterOperator,
    setFilters,
    setFilterGroups,
    addFilterGroup,
    addFilterToGroup,
    updateFilterInGroup,
    removeFilterFromGroup,
    updateGroupOperator,
    removeFilterGroup,
    setGroupsOperator,
  } = useFilterStore(searchType);
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const { paginationModel, setPaginationModel } = useGridStore();

  const { basicSearchInput, setBasicSearch, setOpenResult } = useSearchStore();

  const query = useSearch();
  const searchCount = query.data?.totalCount;
  const searchActive = query.searchActive;

  const { errorShown, showError, hideError } = useSearchPrecheck(
    searchActive && typeof searchCount === 'undefined',
  );

  const searchStartTimeRef = useRef<number | null>(null);
  const { trackEvent } = useDatadogTracker();

  const errorCheck = () => {
    if (typeof searchCount === 'undefined' && !errorShown) {
      return showError();
    }

    if (typeof searchCount !== 'undefined' && errorShown) {
      hideError();
    }
  };

  const handleSearch = () => {
    let error = errorCheck();
    if (!!error) return;

    hideError();
    armSearchHistoryPush(); // committed search → own Back entry
    searchStartTimeRef.current = performance.now();
    setOpen(false);
    setOpenResult(true);
  };

  const handleClose = () => {
    if (aiSearchLoading) {
      cancelPendingAiSearch();
      setAiSearchLoading(false);
    }
    setOpen(false);
  };

  const handleResetFilters = () => {
    abortAllPendingGraphQL();
    clearFilter();
    clearFilterGroups();
    // Keep the results grid open — an empty search renders the default list.
    setOpenResult(true);
    hideError();
    setBasicSearch('');
    setOpen(false);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });

    if (searchType === SEARCH_TYPE.ANIMAL || searchType === SEARCH_TYPE.MEMBERS)
      return;
    handleSearch();
  };

  // Flat mode: a single (quick-search/basic) filter set is represented by the
  // store's flat `filters`, not `filterGroups`. The dialog renders it as one
  // group so the UI is uniform, but edits route to the flat actions and nothing
  // is persisted as `groups`. Adding a second group migrates to grouped mode.
  const isFlatMode = filterGroups.length === 0;
  const displayGroups = useMemo(() => {
    if (!isFlatMode) return filterGroups;
    return [
      {
        filters:
          filters.length > 0
            ? filters
            : [{ field: '', operator: '', value: '' } as FilterInput],
        logicalOperator:
          commonFilterOperator === LogicalOperator.and
            ? LogicalOperator.and
            : LogicalOperator.or,
      },
    ];
  }, [isFlatMode, filterGroups, filters, commonFilterOperator]);

  const totalFilterCount =
    filters.length + filterGroups.reduce((sum, g) => sum + g.filters.length, 0);

  const renderSearchButtonLabel = () => {
    if (searchActive && query.loading) {
      return (
        <>
          <CircularProgress size={12} sx={{ color: 'inherit', mr: 0.75 }} />
          Updating results
        </>
      );
    }
    if (totalFilterCount === 0 && !basicSearchInput) {
      return 'View all results';
    }

    return `View ${(searchCount ?? 0).toLocaleString()} results`;
  };

  const handleUpdateFilter = useCallback(
    (groupIndex: number, filterIndex: number, patch: Partial<FilterInput>) => {
      if (isFlatMode) updateFilter(filterIndex, patch);
      else updateFilterInGroup(groupIndex, filterIndex, patch);
    },
    [isFlatMode, updateFilter, updateFilterInGroup],
  );

  const handleRemoveFilter = useCallback(
    (groupIndex: number, filterIndex: number) => {
      if (isFlatMode) removeFilter(filterIndex);
      else removeFilterFromGroup(groupIndex, filterIndex);
    },
    [isFlatMode, removeFilter, removeFilterFromGroup],
  );

  const handleAddFilter = useCallback(
    (groupIndex: number, filter: FilterInput) => {
      if (isFlatMode) addFilter(filter);
      else addFilterToGroup(groupIndex, filter);
    },
    [isFlatMode, addFilter, addFilterToGroup],
  );

  const handleUpdateOperator = useCallback(
    (groupIndex: number, op: LogicalOperator) => {
      if (isFlatMode) updateCommonFilterOperator(op);
      else updateGroupOperator(groupIndex, op);
    },
    [isFlatMode, updateCommonFilterOperator, updateGroupOperator],
  );

  const handleRemoveGroup = useCallback(
    (groupIndex: number) => {
      removeFilterGroup(groupIndex);
    },
    [removeFilterGroup],
  );

  /**
   * The bottom "+" adds a second filter group. In flat mode this is the point
   * where we materialise `groups`: the current flat `filters` become group 0
   * and a fresh empty group 1 is appended, then flat `filters` are cleared so
   * the search is driven purely by `groups` from here on.
   */
  const handleAddGroup = useCallback(() => {
    if (isFlatMode) {
      const base =
        filters.length > 0
          ? filters
          : [{ field: '', operator: '', value: '' } as FilterInput];
      setFilterGroups([
        { filters: base, logicalOperator: commonFilterOperator },
        {
          filters: [{ field: '', operator: '', value: '' }],
          logicalOperator: LogicalOperator.and,
        },
      ]);
      setFilters([]);
      return;
    }
    addFilterGroup();
  }, [
    isFlatMode,
    filters,
    commonFilterOperator,
    setFilterGroups,
    setFilters,
    addFilterGroup,
  ]);

  useEffect(() => {
    if (
      searchStartTimeRef.current !== null &&
      !query.loading &&
      typeof searchCount === 'number' &&
      orgId
    ) {
      const durationMs = performance.now() - searchStartTimeRef.current;

      trackEvent?.(
        'SearchExecuted',
        {
          searchType,
          searchTerm: '',
          filterGroups,
          filterCount: totalFilterCount,
          resultCount: searchCount,
          triggeredFrom: 'advanced',
          orgId,
          durationMs: Math.round(durationMs),
        },
        EventType.FORM_SUBMIT,
      );

      searchStartTimeRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.loading, searchCount]);

  const loadingState = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        py: 6,
      }}
    >
      <CircularProgress color="info" />
      <Typography variant="labelmedium" sx={{ color: 'text.secondary' }}>
        <FormattedMessage
          id="advancedFilterDialog.generatingFilters"
          defaultMessage="Generating filters from your request…"
        />
      </Typography>
    </Box>
  );

  const filterGroupsBody = (
    <>
      <Stack spacing={0}>
        {displayGroups.map((group, groupIndex) => (
          <Box key={groupIndex}>
            {groupIndex > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                }}
              >
                <Box
                  component="button"
                  onClick={() =>
                    setGroupsOperator(
                      groupsOperator === LogicalOperator.and
                        ? LogicalOperator.or
                        : LogicalOperator.and,
                    )
                  }
                  sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    fontSize: '13px',
                    fontWeight: 700,
                    color: 'info.main',
                    backgroundColor: `${(theme.vars || theme).palette.info.main}10`,
                    borderWidth: 1.5,
                    borderStyle: 'solid',
                    borderColor: 'info.main',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: `${(theme.vars || theme).palette.info.main}20`,
                    },
                  })}
                >
                  {groupsOperator === LogicalOperator.and
                    ? LogicalOperator.and
                    : LogicalOperator.or}
                </Box>
              </Box>
            )}
            <FilterGroupContainer
              groupIndex={groupIndex}
              filters={group.filters}
              logicalOperator={group.logicalOperator}
              showDelete={!isFlatMode && filterGroups.length > 1}
              onUpdateFilter={handleUpdateFilter}
              onRemoveFilter={handleRemoveFilter}
              onAddFilter={handleAddFilter}
              onUpdateOperator={handleUpdateOperator}
              onRemoveGroup={handleRemoveGroup}
            />
          </Box>
        ))}
      </Stack>

      <IconButton
        color="info"
        size="small"
        sx={{ mt: 1.5 }}
        onClick={() => handleAddGroup()}
      >
        <AddIcon />
      </IconButton>

      {query.skipQuery && (
        <Typography
          sx={{ color: 'text.secondary', mt: 1 }}
          variant="labelmedium"
        >
          Some filters are incomplete. Please complete or remove them to see the
          result count.
        </Typography>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box>
          <Typography variant="titlesmall" gutterBottom>
            Advanced filters
          </Typography>
          <Typography sx={{ color: 'grey' }}>
            Build your search query by combining filter groups below.
          </Typography>
        </Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'grey.500',
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ pb: 2 }}>
        {aiSearchLoading ? loadingState : filterGroupsBody}
      </DialogContent>
      <DialogActions>
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            px: 1,
          }}
        >
          <Button
            sx={{ color: 'text.secondary' }}
            onClick={handleResetFilters}
            disabled={aiSearchLoading}
          >
            Reset filters
          </Button>
          {!aiSearchLoading &&
            ((searchActive &&
              (typeof searchCount !== 'undefined' || query.loading)) ||
              !searchActive) &&
            !query.skipQuery && (
              <Button
                color="info"
                disabled={searchActive && query.loading}
                variant="contained"
                onClick={handleSearch}
              >
                {renderSearchButtonLabel()}
              </Button>
            )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
